import { useState } from "react";
import { useForm } from "react-hook-form";
import { assetUrl } from "fusion-core";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { formatDateT } from "../../../../shared/utils/formatters";
import NewDateTimePicker from "../../../../shared/components/newDateTimePicker";
import QuoteStep from "../../enums/QuoteStep";
import { fetchPanelData } from "../../services/quoteService";
import { handleNext } from "../../../../shared/utils/navigationUtils";
import useAppStateStore from "../../../../shared/store/AppStateStore";
import useQuoteStore from "../../store/QuoteStore";
import useBuyStore from "../../../buy/store/BuyStore";
import { fetchOptional, saveMember } from "../../../buy/services/buyService";
import BuyStep from "../../../buy/enums/BuyStep";
import { useNavigate } from "fusion-plugin-react-router";
import { TAXI_QUOTE_BRANDREF } from "../../../../config/config";
import ModalWrapper from "../../../../shared/components/ModalWrapper";
import ModalActionButtons from "../../../../shared/components/ModalActionButtons";

const TqPolicyDateTimeModal = () => {
  const {
    watch,
    setValue,
    control,
    handleSubmit,
    formState: { errors, isValid },
    trigger, // <-- get trigger from useForm
  } = useForm();
  const brandRef = TAXI_QUOTE_BRANDREF;
  const STATE = useAppStateStore();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(
    useQuoteStore?.getState()?.quote?.risk?.startDate
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const onClose = () => {
    STATE.setIspolicyModal(false);
  };

  const handleOptionalFetch = async (STATE, navigate) => {
    const RiskObjectId = useQuoteStore.getState().quote?.risk?.riskObjectId;

    try {
      const jsonData = await fetchOptional({
        brandRef: brandRef,
        RiskObjectId: RiskObjectId,
        RiskObjectHistoryId: 1,
        navigate,
      });

      const buyStore = useBuyStore.getState();
      buyStore.setObject("recommendationsData", jsonData);
      setAddonFields(jsonData);
    } catch (error) {
      console.error("Error fetching recommendations data:", error);
    } finally {
      STATE.setRecommendationsLoading(false);
      STATE.setQuoteLoading(false);
    }
  };

  const handleYesClick = async () => {
    STATE.setQuoteLoading(true);
    STATE.setRecommendationsLoading(true);
    onClose();
    await saveMember(
      useBuyStore.getState().objects.panelMemberReqBody,
      navigate
    );
    await handleOptionalFetch(STATE, navigate);
    handleNext(BuyStep.TB_ADDON_RECOMMENDATIONS);
  };

  const handleConfirmClick = () => {
    const { updateField } = useQuoteStore.getState();
    const newDate = formatDateT(watch("date"));
    updateField("risk.startDate", newDate);
    requote();
    onClose();
  };

  const requote = () => {
    const quoteData = useQuoteStore.getState().quote;
    fetchPanelData(
      null,
      quoteData.risk.riskObjectId,
      quoteData.risk.startDate
    ).then(() => {
      handleNext(QuoteStep.TQ_PANEL, STATE);
    });
    onClose();
    handleNext(QuoteStep.TQ_PANEL_LOADER, STATE);
  };

  return (
    <ModalWrapper
      heading="Confirm Start Date"
      onClose={() => STATE.setIspolicyModal(false)}
    >
      <Helmet>
        <link
          rel="stylesheet"
          href={assetUrl("../../../../styles/TqPolicyDateTimeModal.css")}
        />
      </Helmet>
      <form onSubmit={handleSubmit(handleConfirmClick)}>
        {!isEditMode ? (
          <>
            <div>
              <div className="policy-date-time-div">
                <div className="policy-date-time-header">Cover start date</div>
                <div>
                  {startDate
                    ?.split("T")[0]
                    .split("-")
                    .reverse()
                    .join("/")}
                </div>
              </div>
              <div className="policy-date-time-div">
                <div className="policy-date-time-header">Cover start time</div>
                <div>{startDate?.split("T")[1]}</div>
              </div>
            </div>
            <ModalActionButtons
              label="Are you sure you would like your policy to start at the date and
              time you have selected?"
              primaryText="Yes"
              secondaryText="Edit"
              onPrimaryClick={handleYesClick}
              onSecondaryClick={() => setIsEditMode(true)}
              primaryType="button"
              secondaryType="button"
            />
          </>
        ) : (
          <>
            <div>
              <NewDateTimePicker
                name="date"
                label="When would you like your cover to start?"
                errors={errors.date}
                setValue={setValue}
                control={control}
                rules={{
                  required: "Date is required",
                }}
                watch={watch}
                trigger={trigger}
                defaultValue={watch("date")}
                minDate={new Date()}
                maxDate={
                  new Date(new Date().setDate(new Date().getDate() + 29))
                }
              />
              <ModalActionButtons
                label="A change in cover start date may result in a change in price."
                primaryText="Confirm"
                secondaryText="Cancel"
                onPrimaryClick={isValid ? handleConfirmClick : undefined}
                onSecondaryClick={() => setIsEditMode(false)}
                primaryType="submit"
                secondaryType="button"
                primaryDisabledStyle={!isValid}
              />
            </div>
          </>
        )}
      </form>
    </ModalWrapper>
  );
};

export default TqPolicyDateTimeModal;
